<script setup lang="ts">
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import LoginStepWrapper from '../LoginStepWrapper.vue';
import { type AuthStep } from '@store/auth';
import { MrInput } from '#components';

const { mobile, token } = defineProps<{
    mobile: string;
    token: string;
}>();

const emit = defineEmits<{
    submit: [step: AuthStep];
    back: [];
}>();

const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
const showPassword = ref(false);
const showRepeatPassword = ref(false);
const { loading, error } = classToRefs(authStore);
const input = ref<InstanceType<typeof MrInput>>();
const gtm = useGtm();
const changedPassword = ref('');
const changedPasswordConfirm = ref('');
const submitForm = async () => {
    analyticsStore.trackAll('password_reset_submit');
    try {
        const success = await authStore.changePasswordByToken({
            password: changedPassword.value,
            passwordConfirm: changedPasswordConfirm.value,
            mobile,
            token,
        });
        analyticsStore.trackAll('password_reset_result', {
            status: success === false ? 'validation_failed' : 'success',
        });
        if (success) {
            gtm?.trackEvent({
                event: 'confirm change pass',
            });
        }
        if (success !== false) {
            emit('submit', success);
        }
    } catch (e) {
        analyticsStore.trackAll('password_reset_result', { status: 'failed' });
        reportException(e);
    }
};

watch(
    [changedPassword, changedPasswordConfirm],
    ([password, passwordConfirm]) =>
        authStore.passWordValidation({ password, passwordConfirm })
);

const disabled = computed(
    () =>
        !!error.value?.NewPassword ||
        !!error.value?.NewPasswordConfirm ||
        !changedPassword.value ||
        !changedPasswordConfirm.value
);
onMounted(() => input.value?.focus());
</script>

<template>
    <!-- Change password form -->
    <LoginStepWrapper
        title="تعیین رمز عبور جدید"
        sub-title="  رمز عبور جدید و تکرار آن را وارد کنید."
        @submit="submitForm"
    >
        <template #inputs="{ focus }">
            <input type="text" class="hidden" name="username" :value="mobile" />
            <mr-input
                ref="input"
                v-model="changedPassword"
                :type="showPassword ? undefined : 'password'"
                :icon="showPassword ? faEyeSlash : faEye"
                placeholder="رمز عبور جدید"
                dir="ltr"
                required
                :error-text="error['NewPassword']"
                :hide-description="!error['NewPassword']"
                :invalid="!!error['NewPassword']"
                @click:icon="showPassword = !showPassword"
                @focus="focus"
                @submit="submitForm"
            />
            <mr-input
                v-model="changedPasswordConfirm"
                :type="showRepeatPassword ? undefined : 'password'"
                :icon="showRepeatPassword ? faEyeSlash : faEye"
                required
                dir="ltr"
                :error-text="error['NewPasswordConfirm']"
                :hide-description="!error['NewPasswordConfirm']"
                :invalid="!!error['NewPasswordConfirm']"
                placeholder="تکرار رمز عبور جدید"
                @click:icon="showRepeatPassword = !showRepeatPassword"
                @focus="focus"
                @submit="submitForm"
            />
        </template>
        <template #actions>
            <mr-button
                id="login-btn-change"
                full-width
                type="submit"
                :loading
                :disabled
            >
                تایید رمز عبور جدید
            </mr-button>
        </template>
    </LoginStepWrapper>
</template>

<style lang="scss" scoped>
:deep(.input-container) {
    background-color: white !important;
    border-radius: 8px;
}
.hidden {
    display: none;
}
</style>
